import { Component } from '@theme/component';

/**
 * Bottom-pinned CTA for the single-concern page.
 *
 * Two observers, one rule: show the bar only in the stretch of the page between the hero and
 * the products section. Above the fold it would duplicate the hero's own buttons; over the
 * products it would cover them.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bar - The fixed bar.
 * @property {HTMLAnchorElement} link - The button, so its target can be checked at connect time.
 *
 * @extends {Component<Refs>}
 */
class ConcernStickyCta extends Component {
  requiredRefs = ['bar', 'link'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #heroPassed = false;
  #productsInView = false;

  connectedCallback() {
    super.connectedCallback();

    const hero = document.getElementById('concern-hero');
    const products = document.getElementById('concern-products');

    // With nothing to scroll to there is no CTA worth pinning.
    if (!products) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    // No hero on the page means nothing to wait for.
    this.#heroPassed = !hero;

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0 });

    if (hero) this.#observer.observe(hero);
    this.#observer.observe(products);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target.id === 'concern-hero') this.#heroPassed = !entry.isIntersecting;
      else if (entry.target.id === 'concern-products') this.#productsInView = entry.isIntersecting;
    }

    this.#sync();
  };

  #sync() {
    const visible = this.#heroPassed && !this.#productsInView;

    this.refs.bar.dataset.visible = visible ? 'true' : 'false';
  }
}

if (!customElements.get('concern-sticky-cta-component')) {
  customElements.define('concern-sticky-cta-component', ConcernStickyCta);
}
